import { Box, TextField, Typography, useTheme } from '@mui/material';
import type React from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface TimeInputProps {
  value?: string;
  onChange?: (value: string) => void;
  label?: string;
  className?: string;
}

export const padNumber = (num: string): string => {
  const n = Number.parseFloat(num) || 0;
  return n.toString().padStart(2, '0');
};

export default function TimeInput({ value = '0:0', onChange, label, className }: TimeInputProps) {
  const { t } = useTranslation('humanResource');
  const theme = useTheme();

  const [hours, setHours] = useState<string>('0');
  const [minutes, setMinutes] = useState<string>('0');

  useEffect(() => {
    if (value) {
      const [h, m] = value.split(':');
      setHours(h || '0');
      setMinutes(m || '0');
    }
  }, [value]);

  const handleHoursChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    if (val === '' || /^\d+$/.test(val)) {
      setHours(val);
      onChange?.(`${padNumber(val)}:${padNumber(minutes)}`);
    }
  };

  const handleMinutesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    if (val === '' || (/^\d+$/.test(val) && Number.parseFloat(val) <= 59)) {
      setMinutes(val);
      onChange?.(`${padNumber(hours)}:${padNumber(val)}`);
    }
  };

  return (
    <Box className={className}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2, width: '100%' }}>
        {label && (
          <Typography variant="fieldHeader" sx={{ whiteSpace: 'nowrap', mt: 1 }} width={theme.spacing(12)}>
            {label}
          </Typography>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TextField
            type="text"
            inputMode="numeric"
            value={hours}
            onChange={handleHoursChange}
            placeholder="0"
            size="small"
            sx={{
              width: 100,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
              },
            }}
            inputProps={{
              style: {
                textAlign: 'center',
              },
            }}
          />
          <Typography variant="fieldHeader" sx={{ whiteSpace: 'nowrap', mt: 1 }}>
            {t('hours.only')}
          </Typography>
        </Box>
        <Typography variant="fieldHeader" sx={{ whiteSpace: 'nowrap', mt: 1 }}>
          {t('and')}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TextField
            type="text"
            inputMode="numeric"
            value={minutes}
            onChange={handleMinutesChange}
            placeholder={t('minutesPlaceholder') as string}
            size="small"
            sx={{
              width: 100,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
              },
            }}
            inputProps={{
              style: {
                textAlign: 'center',
              },
            }}
          />
          <Typography variant="fieldHeader" sx={{ whiteSpace: 'nowrap', mt: 1 }}>
            {t('minutes')}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
